import React from 'react';
import * as ArgumentTypes from './ExportToPDFArgumentTypes';
import { getCurrentDateTimeFileName } from '../../Utils/dateUtils';
import { getServerURL } from '../../Utils/getServerURL';

interface IProps<T> {
  children: (
    exportToPDF: (args: T) => void,
    setLoading?: (loading: boolean) => void
  ) => React.ReactNode;
  setLoading?: (loading: boolean) => void;
}

export type ExportToPDFFnType<T> = (
  args: T,
  setLoading?: (loading: boolean) => void
) => void;

/** Möjliga typer för argumenten som skickas in till pdf handlern (|'as) */
type PDFArgumentType =
  | ArgumentTypes.IEkonomiskAnalysPDF
  | ArgumentTypes.IEkonomiskAnalysChartPDF
  | ArgumentTypes.IFakturaBildPDF
  | ArgumentTypes.IEkonomiskAnalysVerifikationPDF
  | ArgumentTypes.IEKonomiskAnalysBokfOrderUnderlagPDF;
/**
 * Tillhandahåller en funktion för att ladda ner en rapport till PDF fil
 *
 * Reportname måste matcha med backendes PDFHandler's registrerade report name.
 * Vid ny PDFHandlder: lägg till namnet i PDFReportName typen, skapa interface för typen i ExportToPDFArgumentTypes
 * och |'a med PDFArgumentType
 */
class ExportToPDF<T extends PDFArgumentType> extends React.PureComponent<
  IProps<T>
> {
  exportToPDF: ExportToPDFFnType<T> = (args, setLoading) => {
    const { reportName, fileName, ...handlerArgs } = args;

    if ('download' in HTMLAnchorElement.prototype) {
      if (this.props.setLoading) {
        this.props.setLoading(true);
      } else if (setLoading) {
        setLoading(true);
      }

      fetch(`${getServerURL()}/pdf?reportName=${reportName}`, {
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(handlerArgs),
      }).then(response => {
        if (!response.ok) {
          return;
        }
        response.blob().then(blob => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          // For internet explorer
          const nav = navigator as Navigator & { msSaveBlob?: (blob: Blob, defaultName?: string) => boolean; msSaveOrOpenBlob?: (blob: Blob, defaultName?: string) => boolean };
          if (nav.msSaveBlob || nav.msSaveOrOpenBlob) {
            nav.msSaveOrOpenBlob?.(
              blob,
              `${fileName} ${getCurrentDateTimeFileName()}.pdf`
            );
          } else {
            const link = document.createElement('a');
            link.href = url;
            link.download = `${fileName} ${getCurrentDateTimeFileName()}.pdf`;
            document.body.appendChild(link);
            link.click();
          }

          if (this.props.setLoading) {
            this.props.setLoading(false);
          } else if (setLoading) {
            setLoading(false);
          }
        });
      });
    } else {
      const fileNameWithDate = `${fileName} ${getCurrentDateTimeFileName()}.pdf`;

      const form = document.createElement('form');
      form.action = `${getServerURL()}/pdf?reportName=EkonomiskAnalys`;
      form.method = 'post';
      form.target =
        'download' in HTMLAnchorElement.prototype ? '_self' : '_blank';

      const input = document.createElement('input');
      input.name = 'json';
      input.value = JSON.stringify({
        ...handlerArgs,
        fileName: fileNameWithDate,
      });
      input.type = 'hidden';

      form.appendChild(input);
      document.body.appendChild(form);

      form.submit();

      document.body.removeChild(form);
    }
  };

  render() {
    return this.props.children(this.exportToPDF);
  }
}

export default ExportToPDF;
